import { useMemo, useRef } from "react";
import { X, XSquare } from "lucide-react";
import { useTabs, type AppTab } from "./tabs-context";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Fixed-width class shared by every tab so a single very long title cannot
// push neighbours off-screen or distort the bar. Width is generous enough
// for ~22 chars at the current font; longer titles truncate with ellipsis
// and surface in the hover tooltip below.
const uniformTabWidth = "w-36 min-w-[9rem] max-w-[9rem] shrink-0 md:w-44 md:min-w-[11rem] md:max-w-[11rem]";

function isUsableTab(value: unknown): value is AppTab {
  if (!value || typeof value !== "object") return false;
  const tab = value as Partial<AppTab>;
  return (
    typeof tab.id === "string" &&
    tab.id.trim().length > 0 &&
    typeof tab.path === "string" &&
    tab.path.trim().length > 0 &&
    typeof tab.title === "string" &&
    tab.title.trim().length > 0
  );
}

export function selectVisibleTabs(tabs: AppTab[], activeId: string | null): AppTab[] {
  const validTabs = tabs.filter(isUsableTab);
  if (validTabs.length <= 8) return validTabs;
  const pinned = validTabs[0];
  if (!pinned) return [];
  const tail = validTabs.slice(-7);
  const activeTab = activeId ? validTabs.find((tab) => tab.id === activeId) : undefined;
  const candidates =
    activeId === pinned.id || tail.some((tab) => tab.id === activeId) || !activeTab
      ? [pinned, ...tail]
      : [pinned, ...validTabs.slice(-6), activeTab];
  const seen = new Set<string>();
  return candidates.filter((tab) => {
    if (!isUsableTab(tab)) return false;
    if (seen.has(tab.id)) return false;
    seen.add(tab.id);
    return true;
  });
}

export function TabBar() {
  const { tabs, activeId, focusTab, closeTab, closeAllTabs } = useTabs();
  const visibleTabs = useMemo(() => selectVisibleTabs(tabs, activeId), [tabs, activeId]);
  // Roving-focus refs so arrow keys can move keyboard focus between tabs
  // per the ARIA tablist pattern. Keyed by tab id; entries are pruned
  // automatically because ref callbacks run with null on unmount.
  const tabRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  if (tabs.length === 0) return null;

  // ARIA tablist keyboard pattern (manual activation): Left/Right arrows
  // move focus between tabs, Home/End jump to first/last, Enter/Space
  // activates the focused tab. Click behaviour is unchanged.
  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, tabId: string) => {
    const idx = tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return;
    let targetIdx: number | null = null;
    switch (e.key) {
      case "ArrowRight":
        targetIdx = (idx + 1) % tabs.length;
        break;
      case "ArrowLeft":
        targetIdx = (idx - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        targetIdx = 0;
        break;
      case "End":
        targetIdx = tabs.length - 1;
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        focusTab(tabId);
        return;
      default:
        return;
    }
    e.preventDefault();
    const target = tabs[targetIdx];
    if (target) tabRefs.current.get(target.id)?.focus();
  };
  // Count non-pinned tabs to decide whether the "Close all" control is
  // useful. When there's only the pinned dashboard there is nothing to
  // close, so we hide the button to avoid a dead control.
  const closableCount = tabs.filter((t) => !t.pinned).length;
  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex items-stretch"
        data-testid="tab-bar"
      >
        <div
          className="flex items-stretch overflow-x-auto no-scrollbar flex-1"
          role="tablist"
          aria-label="Open tabs"
        >
          {visibleTabs.map((t) => {
            const isActive = t.id === activeId;
            return (
              <Tooltip key={t.id}>
                <TooltipTrigger asChild>
                  <div
                    role="tab"
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    ref={(el) => {
                      if (el) tabRefs.current.set(t.id, el);
                      else tabRefs.current.delete(t.id);
                    }}
                    data-testid={`tab-${t.id}`}
                    onClick={() => focusTab(t.id)}
                    onKeyDown={(e) => handleTabKeyDown(e, t.id)}
                    className={cn(
                      "group flex min-h-11 items-center gap-1.5 px-2 py-1.5 text-xs border-r select-none cursor-pointer md:gap-2 md:px-3 md:py-2 md:text-sm",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                      uniformTabWidth,
                      isActive
                        ? "bg-background font-medium text-foreground border-b-2 border-b-brand-gold -mb-px"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/40",
                    )}
                  >
                    <span className="truncate flex-1 overflow-hidden whitespace-nowrap">
                      {t.title}
                    </span>
                    {t.dirty ? (
                      <span
                        className="h-1.5 w-1.5 rounded-full bg-brand-gold shrink-0"
                        aria-label="Unsaved changes"
                      />
                    ) : null}
                    {!t.pinned ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeTab(t.id);
                        }}
                         className="flex min-h-8 min-w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground opacity-60 group-hover:opacity-100 shrink-0 md:min-h-0 md:min-w-0 md:p-0.5"
                        aria-label={`Close ${t.title}`}
                        data-testid={`tab-close-${t.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-md">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground">{t.path}</div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        {closableCount > 0 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => closeAllTabs()}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 border-l shrink-0 flex items-center gap-1.5"
                data-testid="tab-close-all"
                aria-label="Close all tabs except Dashboard"
              >
                <XSquare className="h-4 w-4" />
                <span className="hidden sm:inline">Close all</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Close every tab except Dashboard
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
